import { useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import {
	Combobox,
	ComboboxComponents,
	type ComboboxComponentsNamespace,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem
} from '@/components/ui/combobox'
import { ChevronDownIcon, XCloseIcon } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface QueueFilterSelectProps<T> {
	placeholder: string
	icon: React.ReactNode
	value: string | null
	onChange: (value: string | null) => void
	items: T[]
	isLoading: boolean
	isFetchingNextPage: boolean
	hasNextPage: boolean
	fetchNextPage?: () => void
	getItemId: (item: T) => string
	getItemLabel: (item: T) => string
	onSearch: (search: string) => void
}

export function QueueFilterSelect<T>({
	placeholder,
	icon,
	value,
	onChange,
	items,
	isLoading,
	isFetchingNextPage,
	hasNextPage,
	fetchNextPage,
	getItemId,
	getItemLabel,
	onSearch
}: QueueFilterSelectProps<T>) {
	const { t } = useTranslation()

	const debouncedSearch = useMemo(
		() =>
			debounce((val: string) => {
				onSearch(val)
			}, 300),
		[onSearch]
	)

	// The selected item drops out of `items` whenever the list is re-fetched for
	// a search term or sits on a page that was never loaded. Remembering the
	// label it had when it was picked keeps the trigger — and its clear button —
	// showing the selection instead of falling back to the placeholder.
	const [lastSelectedLabel, setLastSelectedLabel] = useState<string | null>(
		null
	)

	const selectedLabel = useMemo(() => {
		if (!value) {
			return null
		}
		const item = items.find((i) => getItemId(i) === value)
		return item ? getItemLabel(item) : lastSelectedLabel
	}, [value, items, getItemId, getItemLabel, lastSelectedLabel])

	const handleValueChange = (
		newValue: string | null,
		_eventDetails: ComboboxComponentsNamespace.Root.ChangeEventDetails
	) => {
		const newItem = newValue
			? items.find((i) => getItemId(i) === newValue)
			: undefined
		setLastSelectedLabel(newItem ? getItemLabel(newItem) : null)
		onChange(newValue)
	}

	return (
		<Combobox
			modal
			onInputValueChange={(inputValue: string) =>
				debouncedSearch(inputValue)
			}
			onValueChange={handleValueChange}
			value={value}
		>
			<ComboboxComponents.Trigger
				className={cn(
					'flex h-auto min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-3 py-2 text-sm transition-all duration-150',
					'hover:not-disabled:bg-neutral-50',
					'hover:not-disabled:shadow-focus-primary-100-xxs hover:not-disabled:outline hover:not-disabled:outline-1 hover:not-disabled:outline-neutral-300',
					'focus:shadow-focus-primary-100-xxs',
					'data-[popup-open]:shadow-focus-primary-100-xxs data-[popup-open]:outline data-[popup-open]:outline-1 data-[popup-open]:outline-neutral-300'
				)}
				nativeButton={false}
				render={
					<div>
						{value ? (
							<span className='flex min-w-0 items-center gap-2 [&>svg]:shrink-0'>
								{icon}
								<span className='truncate'>
									{selectedLabel ?? placeholder}
								</span>
								<button
									className='flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700'
									onClick={() => onChange(null)}
									onPointerDown={(e: React.MouseEvent) => {
										e.stopPropagation()
										e.preventDefault()
									}}
									type='button'
								>
									<XCloseIcon size={14} />
								</button>
							</span>
						) : (
							<span className='flex min-w-0 items-center gap-2 font-medium text-neutral-600 [&>svg]:shrink-0'>
								{icon}
								<span className='truncate text-neutral-900'>
									{placeholder}
								</span>
							</span>
						)}
						<ComboboxComponents.Icon className='ml-auto shrink-0 text-neutral-500'>
							<ChevronDownIcon
								className='transition-transform duration-150 [[data-popup-open]_&]:rotate-180'
								size={16}
							/>
						</ComboboxComponents.Icon>
					</div>
				}
			/>
			<ComboboxContent className='w-(--anchor-width) min-w-60'>
				<ComboboxInput placeholder={t('luxe_submissions:search')} />
				<div className='max-h-[200px] overflow-hidden'>
					{isLoading && (
						<div className='flex flex-col gap-1 p-1'>
							<Skeleton height={36} width='100%' />
							<Skeleton height={36} width='100%' />
							<Skeleton height={36} width='100%' />
							<Skeleton height={36} width='100%' />
							<Skeleton height={36} width='100%' />
						</div>
					)}
					{!isLoading && items.length === 0 && (
						<div className='p-2 text-center text-neutral-500 text-sm'>
							No results found
						</div>
					)}
					{!isLoading && items.length > 0 && (
						<Virtuoso
							className='!h-[200px] [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'
							data={items}
							endReached={() => {
								if (
									hasNextPage &&
									!isFetchingNextPage &&
									fetchNextPage
								) {
									fetchNextPage()
								}
							}}
							itemContent={(_, item) => (
								<ComboboxItem
									className='whitespace-normal!'
									key={getItemId(item)}
									value={getItemId(item)}
								>
									{getItemLabel(item)}
								</ComboboxItem>
							)}
						/>
					)}
					{isFetchingNextPage && (
						<div className='p-1'>
							<Skeleton height={36} width='100%' />
						</div>
					)}
				</div>
			</ComboboxContent>
		</Combobox>
	)
}
